import React from 'react';
import {
  Pencil,
  Trash,
  ShoppingBag,
  CalendarBlank,
  Tag,
  Warning,
  Clock,
  CheckCircle,
} from 'phosphor-react';
import { UserProduct } from '../../types/entities';
import { ExpiryStatus } from '../../types/enums';
import { formatDate } from '../../use/useProducts';
import LazyImage from '../ui/LazyImage';

interface ExpiryBadgeProps {
  expiryStatus?: ExpiryStatus | string | null;
  expiresAt?: string | null;
}

function ExpiryBadge({ expiryStatus, expiresAt }: ExpiryBadgeProps) {
  if (!expiryStatus || expiryStatus === ExpiryStatus.NO_DATE) return null;

  const config: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
    EXPIRED: {
      label: 'Vencido',
      icon: <Warning className="w-3 h-3" />,
      className: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800',
    },
    EXPIRING_SOON: {
      label: 'Vence em breve',
      icon: <Clock className="w-3 h-3" />,
      className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800',
    },
    OK: {
      label: `Válido até ${formatDate(expiresAt)}`,
      icon: <CheckCircle className="w-3 h-3" />,
      className: 'bg-sage-100 dark:bg-sage-900/30 text-sage-700 dark:text-sage-400 border border-sage-200 dark:border-sage-800',
    },
  };

  const entry = config[expiryStatus];
  if (!entry) return null;
  const { label, icon, className } = entry;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${className}`}>
      {icon}
      {label}
    </span>
  );
}

interface LibraryProductCardProps {
  product: UserProduct;
  onEdit: (product: UserProduct) => void;
  onDelete: (product: UserProduct) => void;
  /** Abre a ficha completa do produto. */
  onOpen?: (product: UserProduct) => void;
}

export default function LibraryProductCard({ product, onEdit, onDelete, onOpen }: LibraryProductCardProps) {
  const status = product.expiryStatus;

  const borderColor =
    status === ExpiryStatus.EXPIRED
      ? 'border-l-red-400'
      : status === ExpiryStatus.EXPIRING_SOON
      ? 'border-l-amber-400'
      : 'border-l-nude-200 dark:border-l-gray-700';

  return (
    <div
      className={`
        bg-white dark:bg-gray-800
        rounded-2xl shadow-soft dark:shadow-none
        border border-nude-100 dark:border-gray-700
        border-l-4 ${borderColor}
        p-4 flex gap-3
        transition-[transform,box-shadow] duration-200 ease-soft hover:shadow-soft-md active:scale-[0.97]
      `}
    >
      {/* Foto, ou o ícone de sempre para quem não mandou nenhuma */}
      <LazyImage
        src={product.imageUrl}
        alt={product.name}
        className={`flex-shrink-0 w-10 h-10 rounded-xl ${product.imageUrl ? 'bg-nude-50 dark:bg-gray-700' : 'bg-blush-50 dark:bg-blush-900/20'}`}
        imgClassName="w-full h-full object-contain"
        fallback={<ShoppingBag className="w-5 h-5 text-blush-400" />}
      />

      {/* Conteúdo */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <button
              onClick={() => onOpen?.(product)}
              disabled={!onOpen}
              className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-tight truncate text-left disabled:cursor-default hover:text-blush-500 disabled:hover:text-gray-700 transition-colors"
            >
              {product.name}
            </button>
            {product.brand && (
              <p className="text-xs text-nude-500 dark:text-gray-400 mt-0.5 truncate">
                {product.brand}
              </p>
            )}
          </div>
          {/* Ações */}
          <div className="flex-shrink-0 flex items-center gap-0.5">
            <button
              onClick={() => onEdit(product)}
              className="p-1.5 text-nude-400 dark:text-gray-500 hover:text-sage-600 dark:hover:text-sage-400 hover:bg-sage-50 dark:hover:bg-sage-900/20 rounded-lg transition-colors"
              aria-label="Editar produto"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(product)}
              className="p-1.5 text-nude-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              aria-label="Excluir produto"
            >
              <Trash className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Info chips */}
        <div className="mt-2 flex flex-wrap gap-1.5 items-center">
          {product.productCategoryName && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-lavender-50 dark:bg-lavender-900/20 text-lavender-600 dark:text-lavender-400 rounded-full text-[10px] font-medium border border-lavender-100 dark:border-lavender-800/50">
              <Tag className="w-2.5 h-2.5" />
              {product.productCategoryName}
            </span>
          )}
          {product.expiresAt && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-nude-50 dark:bg-gray-700 text-nude-600 dark:text-gray-400 rounded-full text-[10px] font-medium border border-nude-100 dark:border-gray-600">
              <CalendarBlank className="w-2.5 h-2.5" />
              Vence em {product.expiresAt.split('-').reverse().join('/')}
            </span>
          )}
          <ExpiryBadge expiryStatus={product.expiryStatus} expiresAt={product.expiresAt} />
        </div>

        {/* Notas */}
        {product.notes && (
          <p className="mt-2 text-xs text-nude-400 dark:text-gray-500 leading-relaxed line-clamp-2">
            {product.notes}
          </p>
        )}
      </div>
    </div>
  );
}
